import "./saveNotices.css";
import type { SaveProblem } from "../infra/save/saveErrors";
import { btnHtml, ribbonHtml } from "./kit";
import { showDialog } from "./dialog";
import { toast } from "./toast";

/**
 * What the player is told when the save runs into trouble (FINISH_PLAN WP4.1, the quota UI and the corrupt-save
 * notice): each typed problem from the save store (infra/save/saveErrors.ts) gets one plain message, at most once a
 * session per kind, and never interrupts a battle with anything but a toast.
 */
export interface SaveNoticeActions {
  /** The save as text (Settings → Save backup's export). */
  backupText(): string;
  /** The damaged copy SaveService kept, if any. */
  damagedText(key: string): string | null;
  share(text: string): Promise<"shared" | "copied" | "failed">;
  /** Reloads the page (this tab becomes the save's writer again). */
  reload(): void;
}

export class SaveNotices {
  private readonly shown = new Set<string>();
  constructor(private readonly parent: HTMLElement, private readonly actions: SaveNoticeActions) {}

  show(p: SaveProblem): void {
    const once = p.kind === "corrupt" ? `corrupt-${p.copy}` : p.kind;
    if (this.shown.has(once)) return;
    this.shown.add(once);
    switch (p.kind) {
      case "locked": this.locked(); break;
      case "quota": void this.quota(); break;
      case "tooLarge":
        toast(this.parent, p.shed ? `The war chronicle grew too long: ${p.shed} was trimmed to keep your progress` : "Your chronicle is too long to keep: make a backup in Settings → Back up progress", { ms: 6000 });
        break;
      case "corrupt":
        if (p.copy === "record") void this.unreadable(p.backupKey);
        else toast(this.parent, "A torn page of your progress was mended from its copy", { ms: 4000 });
        break;
      case "backend":
        break;   // the localStorage copy still holds the save; Copy diagnostics lists the failure
    }
  }

  private locked(): void {
    const cover = document.createElement("div");
    cover.className = "save-locked";
    cover.innerHTML = `<div class="panel dialog">${ribbonHtml("OPEN ELSEWHERE", "", "dtitle disp")}<p>The game is open in another tab, and that one keeps your progress now. This one stands down so the two never clash.</p><div class="row">${btnHtml("PLAY HERE", "gold", { className: "here disp accent" })}</div></div>`;
    cover.querySelector<HTMLButtonElement>(".here")!.onclick = () => this.actions.reload();
    this.parent.append(cover);
  }

  private async quota(): Promise<void> {
    const keep = await showDialog(this.parent, { title: "NO ROOM LEFT", body: "Your phone is out of space, so your latest progress can't be kept. Free some space, and make a backup now.", confirm: "BACK UP", cancel: "LATER" });
    if (keep) await this.share(this.actions.backupText(), "Progress copied: keep it somewhere safe");
  }

  private async unreadable(backupKey: string): Promise<void> {
    const damaged = this.actions.damagedText(backupKey);
    const copy = await showDialog(this.parent, { title: "PROGRESS DAMAGED", body: "Your progress couldn't be read, so a fresh campaign began. The damaged copy is kept: restore a backup in Settings → Back up progress, or copy it for a bug report.", confirm: "COPY IT", cancel: "OK" });
    if (copy && damaged) await this.share(damaged, "Damaged copy taken");
  }

  private async share(text: string, copied: string): Promise<void> {
    const how = await this.actions.share(text);
    if (how === "copied") toast(this.parent, copied);
    else if (how === "failed") toast(this.parent, "Couldn't copy it: try Settings → Back up progress");
  }
}
